import React from 'react';
import fullSlides, {
  SlideHead, Hero, Motif, Pulse, d,
  GeologyCrossSection, WaterfallChart,
} from './PilotSlides';

/* ============================================================
   Sequester CCS - Pilot PITCH cut (route: /pilotpres)
   A punchier, shorter narrative for in-person / online delivery.
   Reuses the polished slide bodies from the full deck in a tighter
   order, swaps in presentation-first variants of a few key slides,
   and parks the technical / reference slides AFTER the closing
   contact slide as backup to walk through on request.
   ============================================================ */

const byTitle = Object.fromEntries(fullSlides.map((s) => [s.title, s]));
const pick = (...titles) => titles.map((t) => {
  const s = byTitle[t];
  if (!s) throw new Error(`PilotPresSlides: missing slide "${t}"`);
  return s;
});

/* Title: bigger three-part promise, no quote (the presenter speaks it). */
const presTitle = {
  title: 'Title',
  slideClass: 'title-slide title-slide--pres',
  motif: () => <Motif><div className="reservoir-grid" /><Pulse className="top" /></Motif>,
  body: () => (
    <>
      <div className="title-wordmark reveal">SEQUESTER CCS</div>
      <div className="title-tag reveal" style={d(1)}>
        <strong>Permanent</strong> Carbon Storage. <strong>Proven</strong> Technology. <strong>Immediate</strong> Revenue.
      </div>
      <div className="title-rule reveal" style={d(2)} />
      <div className="title-meta reveal" style={d(3)}>
        <div className="sub">Illinois Pilot Project: Investor Briefing</div>
        <div>July 2026</div>
      </div>
    </>
  ),
};

/* Problem: one centred composition, the disparity chart + one XL stat. */
const presProblem = {
  title: 'The Problem',
  motif: () => <Motif><div className="carbon-stream" /></Motif>,
  body: () => (
    <>
      <SlideHead kicker="The Problem" title="37 Billion Tonnes a Year. Almost None Captured." />
      <div className="reveal bar-val--lg" style={{ ...d(1), maxWidth: 760, margin: '0 auto', width: '100%' }}>
        <div className="bars">
          <div className="bar-col">
            <div className="bar-fill blue" style={{ '--bh': '3%' }}><span className="bar-val">50</span></div>
            <div className="bar-name">Today's CCS<br />50 Mtpa</div>
          </div>
          <div className="bar-col">
            <div className="bar-fill grad" style={{ '--bh': '100%' }}><span className="bar-val">7,600</span></div>
            <div className="bar-name">2050 target<br />7,600 Mtpa</div>
          </div>
        </div>
      </div>
      <div className="s-hero s-hero--xl center reveal" style={d(3)}>
        <div className="s-hero-num">99.9%</div>
        <div className="s-hero-label">of global CO₂ emissions go uncaptured. The technology works; the economics have not.</div>
      </div>
    </>
  ),
};

/* Execution, merged for the pitch: transport + injection on one slide. */
const presWellsite = {
  title: 'Pilot Execution: Mine to Reservoir',
  body: () => (
    <>
      <SlideHead kicker="Execution" title="8 km by Road. Then the Reservoir Does the Work." sm
        lede="Decades of extraction left the reservoir under-pressured. It is asking to be filled." />
      <div className="s-split lean-visual">
        <GeologyCrossSection operational />
        <div>
          <Hero value="25 bbl/day" label="post-injection oil, up from 7, alongside permanent CO₂ storage" delay={2} />
          <ul className="s-points reveal" style={{ ...d(3), marginTop: 18 }}>
            <li><strong>$0 pipeline capex:</strong> ~8 km by cryogenic road tanker</li>
            <li><strong>Sub-hydrostatic:</strong> the pressure gradient assists injection</li>
            <li><strong>Metered injection</strong> feeds the 45Q audit trail</li>
          </ul>
        </div>
      </div>
    </>
  ),
};

/* Unit economics: the waterfall alone, full width, one number owns the slide. */
const presEconomics = {
  title: 'Unit Economics',
  motif: () => <Motif><div className="reservoir-grid" /></Motif>,
  body: () => (
    <>
      <SlideHead kicker="Economics" title="$54 Gross Profit Per Tonne. At Every Scale."
        lede="Costs are fixed by contract. The $85 credit is US tax law, not a carbon price." />
      <WaterfallChart maxWidth={720} />
      <Hero value="$54/tonne" label="a 63.5% margin that holds from 12,500 tpa to 9 Mtpa" delay={2} center />
    </>
  ),
};

/* Divider that marks the start of the backup section. */
const divider = {
  title: 'Backup & Detail',
  slideClass: 'divider-slide',
  motif: () => <Motif><div className="reservoir-grid" /></Motif>,
  body: () => (
    <SlideHead
      kicker="Appendix"
      title="Backup & Detail"
      lede="Engineering, geology and financial detail, ready to walk through on request."
    />
  ),
};

const presSlides = [
  /* --- punchy main narrative (16 slides) --- */
  presTitle,
  presProblem,
  ...pick(
    'What Sequester Does',
    'The Aqualung Process',
    'The Pilot Project',
    'Site Overview & Geology',
  ),
  presWellsite,
  presEconomics,
  ...pick(
    'Revenue Model',
    'Pilot Financials',
    'Pilot Timeline & Milestones',
    'Scale-Up Opportunity',
    'Why Now / Competitive Advantage',
    'Risks & De-Risking',
    'Team',
    'The Ask / Next Steps',
  ),

  /* --- backup, after the contact slide --- */
  divider,
  ...pick(
    'The Value Chain, Stage by Stage',
    'Pilot Execution: Capture Site',
    'Pilot Execution: Transport',
    'Process & Instrumentation Diagram',
    'P&ID - Major Equipment',
    'P&ID - Control Loops & Streams',
    'Why Depleted Reservoirs',
    'Sub-Hydrostatic Pressure & EOR',
    'Permanent Storage',
    'Critical Path & Milestones',
    'What the Seed Delivers',
    'Appendix A - Key Numbers',
    'Appendix B - Terminology (1/2)',
    'Appendix B - Terminology (2/2)',
  ),
];

export default presSlides;
